import { Box, ButtonBase, styled } from '@mui/material';
import DESIGN_COLORS from 'shared/_ui/design-colors';

/** Key-info card: white, 1px border, 14px radius. */
export const InfoCard = styled(Box, { name: 'InfoCard' })(({ theme }) => ({
	marginTop: theme.spacing(6),
	marginBottom: theme.spacing(7),
	padding: theme.spacing(4.5, 5),
	border: `1px solid ${DESIGN_COLORS.border}`,
	borderRadius: '0.875rem',
	backgroundColor: theme.palette.background.paper,
	[theme.breakpoints.down('sm')]: { padding: theme.spacing(3.5, 4) },
}));

/** Auto-fit grid of read-only info items. */
export const InfoGrid = styled(Box, { name: 'InfoGrid' })(({ theme }) => ({
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fit, minmax(10.625rem, 1fr))',
	gap: theme.spacing(3.5, 5),
}));

/** Items that collapse on mobile: contents on desktop so they join the parent grid. */
export const MoreItems = styled(Box, {
	name: 'MoreItems',
	shouldForwardProp: (propName: string) => propName !== 'expanded',
})<{ expanded: boolean }>(({ theme, expanded }) => ({
	display: 'contents',
	[theme.breakpoints.down('sm')]: { display: expanded ? 'contents' : 'none' },
}));

/** Blocks (notes, tags) that collapse on mobile. */
export const MoreBlocks = styled(Box, {
	name: 'MoreBlocks',
	shouldForwardProp: (propName: string) => propName !== 'expanded',
})<{ expanded: boolean }>(({ theme, expanded }) => ({
	[theme.breakpoints.down('sm')]: { display: expanded ? 'block' : 'none' },
}));

export const InfoItemRoot = styled(Box, {
	name: 'InfoItem',
	shouldForwardProp: (propName: string) => propName !== 'mono',
})<{ mono?: boolean }>(({ theme, mono }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(2.25),
	minWidth: 0,
	color: DESIGN_COLORS.strong,
	fontSize: '0.875rem',
	fontWeight: 600,
	'& > .MuiSvgIcon-root': {
		flexShrink: 0,
		fontSize: '1.0625rem',
		color: DESIGN_COLORS.faint,
	},
	...(mono && {
		color: DESIGN_COLORS.muted,
		fontFamily: theme.typography.fontFamilyMono,
		fontSize: '0.78rem',
		fontWeight: 500,
	}),
}));

/** Text next to an info icon; truncates instead of wrapping. */
export const InfoItemText = styled('span', { name: 'InfoItemText' })({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

/** Small underlined inline action next to an info value (e.g. "Manage"). */
export const LinkButton = styled(ButtonBase, { name: 'LinkButton' })({
	// The item's flex gap is 0.5625rem; -0.0625rem leaves a 0.5rem gap to the value.
	marginLeft: '-0.0625rem',
	color: DESIGN_COLORS.muted,
	fontSize: '0.71875rem',
	fontWeight: 700,
	textDecoration: 'underline',
	'&:hover': { color: DESIGN_COLORS.strong },
});

/** Full-width "More info / Less info" toggle, mobile only. */
export const ExpandToggle = styled(ButtonBase, {
	name: 'ExpandToggle',
	shouldForwardProp: (propName: string) => propName !== 'open',
})<{ open: boolean }>(({ theme, open }) => ({
	display: 'none',
	width: '100%',
	justifyContent: 'flex-start',
	gap: theme.spacing(1.25),
	paddingTop: theme.spacing(3),
	color: DESIGN_COLORS.muted,
	fontSize: '0.78rem',
	fontWeight: 700,
	'&:hover': { color: DESIGN_COLORS.strong },
	'& .MuiSvgIcon-root': {
		fontSize: '0.8125rem',
		transition: theme.transitions.create('transform', { duration: 200 }),
		transform: open ? 'rotate(180deg)' : 'none',
	},
	[theme.breakpoints.down('sm')]: { display: 'flex' },
}));

/** Block inside the card separated from the content above by a soft top border. */
export const InfoCardSection = styled(Box, { name: 'InfoCardSection' })(({ theme }) => ({
	marginTop: theme.spacing(4),
	paddingTop: theme.spacing(4),
	borderTop: `1px solid ${DESIGN_COLORS.borderSoft}`,
}));

/** Tiny uppercase "Notes" heading with an icon above the notes text. */
export const NotesHead = styled(Box, { name: 'NotesHead' })(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1.75),
	marginBottom: theme.spacing(2),
	color: DESIGN_COLORS.faint,
	fontSize: '0.65625rem',
	fontWeight: 700,
	letterSpacing: '0.025rem',
	textTransform: 'uppercase',
	'& .MuiSvgIcon-root': { fontSize: '0.875rem' },
}));

/** Top row of the info card with the storage switches (Cloud, Local), separated by a soft bottom border. */
export const StorageRow = styled(Box, { name: 'StorageRow' })(({ theme }) => ({
	display: 'flex',
	flexWrap: 'wrap',
	gap: '1.625rem',
	marginBottom: theme.spacing(4),
	paddingBottom: '0.875rem',
	borderBottom: `1px solid ${DESIGN_COLORS.borderSoft}`,
}));

/** Icon + label + switch of one storage location. */
export const StorageToggleRoot = styled('label', { name: 'StorageToggle' })({
	display: 'flex',
	alignItems: 'center',
	gap: '0.5625rem',
	color: DESIGN_COLORS.strong,
	fontSize: '0.84375rem',
	fontWeight: 600,
	'& > .MuiSvgIcon-root': {
		flexShrink: 0,
		fontSize: '1.125rem',
		color: DESIGN_COLORS.muted,
	},
});
